import type { AuditLogDocument } from '@myplayer/schema';

export type AuditResourceTypeFilter = 'ALL' | AuditLogDocument['resourceType'] | (string & {});
export type AuditActorTypeFilter = 'ALL' | AuditLogDocument['actorType'] | (string & {});
export type AuditSortOrder = 'NEWEST' | 'OLDEST';

export interface AuditFacetCounts {
  readonly workspaceCounts: Readonly<Record<string, number>>;
  readonly resourceCounts: Readonly<Record<string, number>>;
  readonly actorCounts: Readonly<Record<string, number>>;
  readonly actionCounts: Readonly<Record<string, number>>;
}

export interface AuditEntryRecord extends AuditLogDocument {
  readonly reason?: string;
  readonly sequence?: number;
  readonly prevHash?: string;
  readonly entryHash?: string;
}

const ACTION_LABELS: Readonly<Record<string, string>> = {
  IDENTIFIER_DRAFT_PROMOTED: 'Product draft promoted to live',
  IDENTIFIER_DRAFT_REJECTED: 'Product draft failed validation',
  IDENTIFIER_CREATED_VALID: 'Product created and promoted',
  IDENTIFIER_CREATED: 'Product draft created',
  IDENTIFIER_UPDATED: 'Product draft updated',
  QUALIFIER_CREATED_VALID: 'Qualifier created and promoted',
  QUALIFIER_DRAFT_PROMOTED: 'Qualifier draft promoted to live',
  QUALIFIER_DRAFT_REJECTED: 'Qualifier draft failed validation',
  SCHEDULED_CHANGE_SET: 'Scheduled link change set',
  SCHEDULED_CHANGE_CANCELLED: 'Scheduled link change cancelled',
  EMERGENCY_RECALL_ACTIVATED: 'Emergency recall activated',
  RECALL_ACTIVATED: 'Emergency recall activated',
  EMERGENCY_RECALL_DEACTIVATED: 'Emergency recall cleared',
  RECALL_CLEARED: 'Emergency recall cleared',
  MEMBERSHIP_INVITE: 'Team member invited',
  MEMBERSHIP_INVITED: 'Team member invited',
  MEMBERSHIP_ACTIVATED: 'Team member activated',
  MEMBERSHIP_UPDATE_ROLES: 'Team member roles updated',
  MEMBERSHIP_UPDATED: 'Team member roles updated',
  MEMBERSHIP_REVOKE: 'Team member access revoked',
  MEMBERSHIP_REVOKED: 'Team member access revoked',
  API_CLIENT_CREATED: 'API key created',
  API_CLIENT_REVOKED: 'API key revoked',
  DOMAIN_CREATED: 'Custom domain registered',
  DOMAIN_VERIFIED: 'Custom domain verified',
  DOMAIN_OFFBOARDED: 'Custom domain removed',
  WORKSPACE_CREATED: 'Workspace created',
  WORKSPACE_UPDATED: 'Workspace settings updated',
  WORKSPACE_ARCHIVED: 'Workspace archived',
  ORGANISATION_UPDATED: 'Organisation profile updated',
  INTEGRATION_GA4_SAVED: 'Google Analytics integration saved',
  INTEGRATION_GA4_DISCONNECTED: 'Google Analytics integration disconnected',
};

export const ALL_AUDIT_ACTION_TYPES: readonly string[] = [
  'IDENTIFIER_DRAFT_PROMOTED',
  'IDENTIFIER_DRAFT_REJECTED',
  'IDENTIFIER_CREATED_VALID',
  'QUALIFIER_CREATED_VALID',
  'QUALIFIER_DRAFT_PROMOTED',
  'QUALIFIER_DRAFT_REJECTED',
  'SCHEDULED_CHANGE_SET',
  'SCHEDULED_CHANGE_CANCELLED',
  'EMERGENCY_RECALL_ACTIVATED',
  'EMERGENCY_RECALL_DEACTIVATED',
  'MEMBERSHIP_INVITED',
  'MEMBERSHIP_ACTIVATED',
  'MEMBERSHIP_UPDATED',
  'MEMBERSHIP_REVOKED',
  'API_CLIENT_CREATED',
  'API_CLIENT_REVOKED',
  'DOMAIN_CREATED',
  'DOMAIN_VERIFIED',
  'DOMAIN_OFFBOARDED',
  'WORKSPACE_CREATED',
  'WORKSPACE_UPDATED',
  'WORKSPACE_ARCHIVED',
  'ORGANISATION_UPDATED',
  'INTEGRATION_GA4_SAVED',
  'INTEGRATION_GA4_DISCONNECTED',
];

const RESOURCE_TYPE_LABELS: Readonly<Record<AuditLogDocument['resourceType'], string>> = {
  ORGANISATION: 'Organisation',
  WORKSPACE: 'Workspace',
  DOMAIN: 'Domain',
  IDENTIFIER: 'Product / Identifier',
  QUALIFIER: 'Qualifier',
  MEMBERSHIP: 'Team Member',
  API_CLIENT: 'API Key',
  RECALL: 'Recall',
};

const ACTOR_TYPE_LABELS: Readonly<Record<AuditLogDocument['actorType'], string>> = {
  USER: 'User',
  API_CLIENT: 'API Key',
  SYSTEM_TRIGGER: 'System',
};

export function formatAuditActionLabel(action: string): string {
  const mapped = ACTION_LABELS[action];
  if (mapped !== undefined) return mapped;
  return action
    .split('_')
    .filter((part) => part.length > 0)
    .map((part) => part.charAt(0).toUpperCase() + part.slice(1).toLowerCase())
    .join(' ');
}

export function formatAuditResourceTypeLabel(
  resourceType: AuditLogDocument['resourceType'],
): string {
  return RESOURCE_TYPE_LABELS[resourceType] ?? resourceType;
}

export function formatAuditActorTypeLabel(actorType: AuditLogDocument['actorType']): string {
  return ACTOR_TYPE_LABELS[actorType] ?? actorType;
}

export function formatAuditTimestamp(timestampIso: string): string {
  const parsed = Date.parse(timestampIso);
  if (Number.isNaN(parsed)) return timestampIso;
  return new Date(parsed)
    .toISOString()
    .replace('T', ' ')
    .replace(/\.\d{3}Z$/, ' UTC');
}

function formatSnapshotValue(value: unknown): string {
  if (value === null || value === undefined) return 'none';
  if (typeof value === 'string') return value.length > 0 ? value : '(empty)';
  if (typeof value === 'number' || typeof value === 'boolean') return String(value);
  if (Array.isArray(value)) return `${String(value.length)} item${value.length === 1 ? '' : 's'}`;
  if (typeof value === 'object') {
    const obj = value as Readonly<Record<string, unknown>>;
    if (typeof obj['active'] === 'boolean') {
      const url = typeof obj['overrideUrl'] === 'string' ? ` -> ${obj['overrideUrl']}` : '';
      return `active: ${String(obj['active'])}${url}`;
    }
    const keys = Object.keys(obj);
    return keys.length > 0 ? `{${keys.slice(0, 3).join(', ')}}` : '{}';
  }
  return String(value);
}

export function formatAuditSnapshotSummary(
  snapshot: Readonly<Record<string, unknown>> | null | undefined,
): string {
  if (snapshot === null || snapshot === undefined) return 'None (new record)';
  const entries = Object.entries(snapshot).filter(([, val]) => val !== undefined);
  if (entries.length === 0) return 'No recorded fields';
  return entries.map(([key, val]) => `${key}: ${formatSnapshotValue(val)}`).join(' · ');
}

export function coerceAuditLogRecord(
  docId: string,
  raw: Readonly<Record<string, unknown>>,
  fallbackOrgId: string,
  fallbackWorkspaceId: string,
): AuditEntryRecord {
  const actorTypeRaw = raw['actorType'];
  const actorType: AuditLogDocument['actorType'] =
    actorTypeRaw === 'API_CLIENT' || actorTypeRaw === 'SYSTEM_TRIGGER' ? actorTypeRaw : 'USER';

  const resourceTypeRaw = raw['resourceType'];
  const validResourceTypes: ReadonlySet<string> = new Set([
    'ORGANISATION',
    'WORKSPACE',
    'DOMAIN',
    'IDENTIFIER',
    'QUALIFIER',
    'MEMBERSHIP',
    'API_CLIENT',
    'RECALL',
  ]);
  const resourceType: AuditLogDocument['resourceType'] =
    typeof resourceTypeRaw === 'string' && validResourceTypes.has(resourceTypeRaw)
      ? (resourceTypeRaw as AuditLogDocument['resourceType'])
      : 'IDENTIFIER';

  const beforeRaw = raw['beforeSnapshot'];
  const afterRaw = raw['afterSnapshot'];

  return {
    auditId:
      typeof raw['auditId'] === 'string' && raw['auditId'].length > 0 ? raw['auditId'] : docId,
    orgId:
      typeof raw['orgId'] === 'string' && raw['orgId'].length > 0 ? raw['orgId'] : fallbackOrgId,
    workspaceId:
      typeof raw['workspaceId'] === 'string' && raw['workspaceId'].length > 0
        ? raw['workspaceId']
        : raw['resourceType'] === 'ORGANISATION' ||
            raw['resourceType'] === 'MEMBERSHIP' ||
            raw['resourceType'] === 'WORKSPACE'
          ? ''
          : fallbackWorkspaceId,
    actorUid: typeof raw['actorUid'] === 'string' ? raw['actorUid'] : 'unknown',
    actorType,
    ...(typeof raw['actorRole'] === 'string'
      ? { actorRole: raw['actorRole'] as NonNullable<AuditLogDocument['actorRole']> }
      : {}),
    action: typeof raw['action'] === 'string' ? raw['action'] : 'UNKNOWN_ACTION',
    resourceType,
    resourceId: typeof raw['resourceId'] === 'string' ? raw['resourceId'] : docId,
    ...(typeof raw['version'] === 'number' ? { version: raw['version'] } : {}),
    beforeSnapshot:
      beforeRaw !== null && typeof beforeRaw === 'object' && !Array.isArray(beforeRaw)
        ? (beforeRaw as Record<string, unknown>)
        : null,
    afterSnapshot:
      afterRaw !== null && typeof afterRaw === 'object' && !Array.isArray(afterRaw)
        ? (afterRaw as Record<string, unknown>)
        : null,
    correlationId: typeof raw['correlationId'] === 'string' ? raw['correlationId'] : '',
    timestamp: typeof raw['timestamp'] === 'string' ? raw['timestamp'] : '',
    ...(typeof raw['reason'] === 'string' ? { reason: raw['reason'] } : {}),
    ...(typeof raw['sequence'] === 'number' && Number.isInteger(raw['sequence'])
      ? { sequence: raw['sequence'] }
      : {}),
    ...(typeof raw['prevHash'] === 'string' && raw['prevHash'].length > 0
      ? { prevHash: raw['prevHash'] }
      : {}),
    ...(typeof raw['entryHash'] === 'string' && raw['entryHash'].length > 0
      ? { entryHash: raw['entryHash'] }
      : {}),
  };
}

export interface AuditLogFilterCriteria {
  readonly workspaceId: string;
  readonly searchQuery: string;
  readonly resourceTypeFilter: AuditResourceTypeFilter;
  readonly actorTypeFilter: AuditActorTypeFilter;
  readonly actionFilter: string;
  readonly sortOrder?: AuditSortOrder;
  readonly fromDate?: string;
  readonly toDate?: string;
}

export const AUDIT_SORT_OPTIONS: readonly {
  readonly value: AuditSortOrder;
  readonly label: string;
}[] = [
  { value: 'NEWEST', label: 'Newest first' },
  { value: 'OLDEST', label: 'Oldest first' },
];

export function computeAuditFacetCounts(
  entries: readonly AuditEntryRecord[],
  effectiveFilterWorkspaceId = '',
): AuditFacetCounts {
  const wsTokens = parseAuditTokens(effectiveFilterWorkspaceId);
  const workspaceCounts: Record<string, number> = {};
  const resourceCounts: Record<string, number> = {};
  const actorCounts: Record<string, number> = {};
  const actionCounts: Record<string, number> = {};
  for (const entry of entries) {
    if (entry.workspaceId) {
      workspaceCounts[entry.workspaceId] = (workspaceCounts[entry.workspaceId] ?? 0) + 1;
    }
    if (
      wsTokens !== null &&
      !wsTokens.has(entry.workspaceId) &&
      entry.workspaceId !== '' &&
      entry.workspaceId !== 'org_global'
    ) {
      continue;
    }
    resourceCounts[entry.resourceType] = (resourceCounts[entry.resourceType] ?? 0) + 1;
    actorCounts[entry.actorType] = (actorCounts[entry.actorType] ?? 0) + 1;
    if (entry.action.trim()) {
      actionCounts[entry.action] = (actionCounts[entry.action] ?? 0) + 1;
    }
  }
  return { workspaceCounts, resourceCounts, actorCounts, actionCounts };
}

function parseAuditTokens(filterValue: string): ReadonlySet<string> | null {
  const trimmed = filterValue.trim();
  if (!trimmed || trimmed === 'ALL') return null;
  const tokens = trimmed
    .split(',')
    .map((t) => t.trim())
    .filter((t) => t.length > 0 && t !== 'ALL');
  return tokens.length > 0 ? new Set(tokens) : null;
}

export const RESOURCE_FILTER_OPTIONS = [
  ['ALL', 'All resources'],
  ['IDENTIFIER', 'Product / Identifier'],
  ['RECALL', 'Recall'],
  ['DOMAIN', 'Domain'],
  ['MEMBERSHIP', 'Team Member'],
  ['API_CLIENT', 'API Key'],
  ['WORKSPACE', 'Workspace'],
  ['QUALIFIER', 'Qualifier'],
  ['ORGANISATION', 'Organisation'],
] as const;

export const ACTOR_FILTER_OPTIONS = [
  ['ALL', 'All actor types'],
  ['USER', 'User'],
  ['API_CLIENT', 'API Key'],
  ['SYSTEM_TRIGGER', 'System'],
] as const;

export function getDistinctAuditActions(
  entries: readonly AuditEntryRecord[],
  effectiveFilterWorkspaceId: string,
): readonly string[] {
  const wsTokens = parseAuditTokens(effectiveFilterWorkspaceId);
  const set = new Set<string>(ALL_AUDIT_ACTION_TYPES);
  for (const entry of entries) {
    if (
      (wsTokens === null ||
        wsTokens.has(entry.workspaceId) ||
        entry.workspaceId === '' ||
        entry.workspaceId === 'org_global') &&
      entry.action.trim()
    ) {
      set.add(entry.action);
    }
  }
  return Array.from(set).sort();
}

export function triggerBrowserCsvDownload(csvContent: string, filename: string): void {
  if (typeof document !== 'undefined' && typeof URL !== 'undefined' && URL.createObjectURL) {
    try {
      const url = URL.createObjectURL(new Blob([csvContent], { type: 'text/csv;charset=utf-8;' }));
      const anchor = document.createElement('a');
      anchor.href = url;
      anchor.download = filename;
      document.body.appendChild(anchor);
      anchor.click();
      document.body.removeChild(anchor);
      URL.revokeObjectURL(url);
    } catch {
      // Ignore DOM download errors in test runners
    }
  }
}

export function filterAndSortAuditEntries(
  entries: readonly AuditEntryRecord[],
  criteria: AuditLogFilterCriteria,
): readonly AuditEntryRecord[] {
  const queryNorm = criteria.searchQuery.trim().toLowerCase();
  const wsTokens = parseAuditTokens(criteria.workspaceId);
  const resourceTokens = parseAuditTokens(criteria.resourceTypeFilter);
  const actorTokens = parseAuditTokens(criteria.actorTypeFilter);
  const actionTokens = parseAuditTokens(criteria.actionFilter.toUpperCase());
  const fromDateNorm = (criteria.fromDate ?? '').trim();
  const toDateNorm = (criteria.toDate ?? '').trim();
  const sortOrder = criteria.sortOrder ?? 'NEWEST';

  return entries
    .filter((entry) => {
      if (
        wsTokens !== null &&
        !wsTokens.has(entry.workspaceId) &&
        entry.workspaceId !== '' &&
        entry.workspaceId !== 'org_global'
      ) {
        return false;
      }
      if (resourceTokens !== null && !resourceTokens.has(entry.resourceType)) {
        return false;
      }
      if (actorTokens !== null && !actorTokens.has(entry.actorType)) {
        return false;
      }
      if (actionTokens !== null && !actionTokens.has(entry.action.toUpperCase())) {
        return false;
      }
      if (fromDateNorm.length > 0 && entry.timestamp.slice(0, 10) < fromDateNorm) {
        return false;
      }
      if (toDateNorm.length > 0 && entry.timestamp.slice(0, 10) > toDateNorm) {
        return false;
      }
      if (queryNorm.length > 0) {
        const searchable = [
          entry.auditId,
          entry.actorUid,
          entry.actorRole ?? '',
          entry.action,
          formatAuditActionLabel(entry.action),
          entry.resourceType,
          entry.resourceId,
          entry.reason ?? '',
          entry.sequence !== undefined
            ? `#${String(entry.sequence)} ${String(entry.sequence)}`
            : '',
          entry.entryHash ?? '',
          entry.prevHash ?? '',
          entry.correlationId,
          formatAuditSnapshotSummary(entry.beforeSnapshot),
          formatAuditSnapshotSummary(entry.afterSnapshot),
        ]
          .join(' ')
          .toLowerCase();
        if (!searchable.includes(queryNorm)) {
          return false;
        }
      }
      return true;
    })
    .slice()
    .sort((a, b) =>
      sortOrder === 'OLDEST'
        ? a.timestamp.localeCompare(b.timestamp)
        : b.timestamp.localeCompare(a.timestamp),
    );
}
